import { UPLOAD_MATCH_TAB_DOM_ID } from "constants/whats-new.constants";
import type { CategoryNavItem } from "./CategoryNav";

export const CATEGORY_TAB_ID = {
  IMAGE: "explore-image",
  APPLICATION: "explore-application",
  EFFECT: "explore-effect",
  BRAND: "explore-brand",
} as const;

export type CategoryTabId =
  (typeof CATEGORY_TAB_ID)[keyof typeof CATEGORY_TAB_ID];

/** Filters the tab set down to the categories currently enabled by feature flags. */
export const getVisibleCategoryNavItems = (
  uploadMatchEnabled: boolean,
  browseEnabled: boolean,
): CategoryNavItem[] =>
  CATEGORY_NAV_ITEMS.filter((item) =>
    item.id === CATEGORY_TAB_ID.IMAGE ? uploadMatchEnabled : browseEnabled,
  );

export const CATEGORY_NAV_ITEMS: CategoryNavItem[] = [
  {
    id: CATEGORY_TAB_ID.IMAGE,
    domId: UPLOAD_MATCH_TAB_DOM_ID,
    icon: (
      <svg
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M15.8333 2.5H4.16667C3.24619 2.5 2.5 3.24619 2.5 4.16667V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5H15.8333C16.7538 17.5 17.5 16.7538 17.5 15.8333V4.16667C17.5 3.24619 16.7538 2.5 15.8333 2.5Z"
          stroke="currentColor"
          strokeWidth="1.45833"
        />
        <path
          d="M2.5 13.3334L6.66667 9.16675L10 12.5001L12.5 10.0001L15 12.5001"
          stroke="currentColor"
          strokeWidth="1.45833"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7.08334 8.33325C7.77369 8.33325 8.33334 7.77361 8.33334 7.08325C8.33334 6.3929 7.77369 5.83325 7.08334 5.83325C6.39298 5.83325 5.83334 6.3929 5.83334 7.08325C5.83334 7.77361 6.39298 8.33325 7.08334 8.33325Z"
          fill="currentColor"
        />
      </svg>
    ),
    tabTitleKey: "ldp_discovery_tab_image_title",
    subTabTitleKey: "ldp_discovery_tab_image_subtitle",
  },
  {
    id: CATEGORY_TAB_ID.APPLICATION,
    icon: (
      <svg
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M7.91667 2.5H3.75C3.05964 2.5 2.5 3.05964 2.5 3.75V7.91667C2.5 8.60702 3.05964 9.16667 3.75 9.16667H7.91667C8.60702 9.16667 9.16667 8.60702 9.16667 7.91667V3.75C9.16667 3.05964 8.60702 2.5 7.91667 2.5Z"
          stroke="currentColor"
          strokeWidth="1.45833"
        />
        <path
          d="M16.2499 2.5H12.0833C11.3929 2.5 10.8333 3.05964 10.8333 3.75V7.91667C10.8333 8.60702 11.3929 9.16667 12.0833 9.16667H16.2499C16.9403 9.16667 17.4999 8.60702 17.4999 7.91667V3.75C17.4999 3.05964 16.9403 2.5 16.2499 2.5Z"
          stroke="currentColor"
          strokeWidth="1.45833"
        />
        <path
          d="M7.91667 10.8333H3.75C3.05964 10.8333 2.5 11.393 2.5 12.0833V16.25C2.5 16.9403 3.05964 17.5 3.75 17.5H7.91667C8.60702 17.5 9.16667 16.9403 9.16667 16.25V12.0833C9.16667 11.393 8.60702 10.8333 7.91667 10.8333Z"
          stroke="currentColor"
          strokeWidth="1.45833"
        />
        <path
          d="M16.2499 10.8333H12.0833C11.3929 10.8333 10.8333 11.393 10.8333 12.0833V16.25C10.8333 16.9403 11.3929 17.5 12.0833 17.5H16.2499C16.9403 17.5 17.4999 16.9403 17.4999 16.25V12.0833C17.4999 11.393 16.9403 10.8333 16.2499 10.8333Z"
          stroke="currentColor"
          strokeWidth="1.45833"
        />
      </svg>
    ),
    tabTitleKey: "ldp_discovery_tab_application_title",
    subTabTitleKey: "ldp_discovery_tab_application_subtitle",
  },
  {
    id: CATEGORY_TAB_ID.EFFECT,
    icon: (
      <svg
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M10 2.5L11.25 6.25H15L11.875 8.51667L13.0667 12.175L10 10.0833L6.93333 12.175L8.125 8.51667L5 6.25H8.75L10 2.5Z"
          stroke="currentColor"
          strokeWidth="1.45833"
          strokeLinejoin="round"
        />
        <path
          d="M4.16669 15.8334L5.41669 14.5834M15.8334 15.8334L14.5834 14.5834M4.16669 4.16675L5.41669 5.41675"
          stroke="currentColor"
          strokeWidth="1.45833"
          strokeLinecap="round"
        />
      </svg>
    ),
    tabTitleKey: "ldp_discovery_tab_effect_title",
    subTabTitleKey: "ldp_discovery_tab_effect_subtitle",
  },
  {
    id: CATEGORY_TAB_ID.BRAND,
    icon: (
      <svg
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M1.66669 14.1667L10 17.5001L18.3334 14.1667"
          stroke="currentColor"
          strokeWidth="1.45833"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M1.66669 10L10 13.3333L18.3334 10"
          stroke="currentColor"
          strokeWidth="1.45833"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M1.66669 5.83333L10 2.5L18.3334 5.83333L10 9.16667L1.66669 5.83333Z"
          stroke="currentColor"
          strokeWidth="1.45833"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    tabTitleKey: "ldp_discovery_tab_brand_title",
    subTabTitleKey: "ldp_discovery_tab_brand_subtitle",
  },
];
